import { resolveStreamUrl } from '@/lib/stream'
import { useCallback, useEffect, useRef, useState } from 'react'

const CONNECT_TIMEOUT_MS = 15000

export function useLivePlayer(streamUrl?: string) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const resolvedRef = useRef('')
  const resolvingRef = useRef<Promise<string> | null>(null)
  const timeoutRef = useRef<number | null>(null)
  const [playing, setPlaying] = useState(false)
  const [loading, setLoading] = useState(false)
  const [volume, setVolumeState] = useState(0.85)
  const [muted, setMuted] = useState(false)
  const [volumeSupported, setVolumeSupported] = useState(true)
  const [error, setError] = useState('')

  const clearConnectTimer = useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  // Descobre o endereço real do stream assim que o link muda, para o clique ser imediato.
  useEffect(() => {
    let cancelled = false
    const raw = (streamUrl || '').trim()
    resolvedRef.current = ''
    if (!raw) {
      resolvingRef.current = null
      return
    }

    const task = resolveStreamUrl(raw)
      .catch((err) => {
        console.error('Erro ao resolver stream:', err)
        return raw
      })
      .then((url) => {
        if (!cancelled) {
          resolvedRef.current = url
          const audio = audioRef.current
          if (audio && audio.paused && url) audio.src = url
        }
        return url
      })
    resolvingRef.current = task

    return () => {
      cancelled = true
    }
  }, [streamUrl])

  useEffect(() => {
    const audio = new Audio()
    audio.preload = 'none'
    audio.setAttribute('playsinline', 'true')
    audioRef.current = audio

    // iPhone/iPad ignoram o volume do navegador: detecta para mostrar mudo em vez do controle.
    try {
      audio.volume = 0.5
      setVolumeSupported(Math.abs(audio.volume - 0.5) < 0.01)
      audio.volume = 0.85
    } catch {
      setVolumeSupported(false)
    }

    const onError = () => {
      clearConnectTimer()
      setPlaying(false)
      setLoading(false)
      setError('Não foi possível reproduzir. Confira o link da radios.net ou o stream direto no painel.')
    }
    const onPause = () => {
      setPlaying(false)
    }
    const onPlaying = () => {
      clearConnectTimer()
      setPlaying(true)
      setLoading(false)
      setError('')
    }
    const onWaiting = () => {
      if (!audio.paused) setLoading(true)
    }

    audio.addEventListener('error', onError)
    audio.addEventListener('pause', onPause)
    audio.addEventListener('playing', onPlaying)
    audio.addEventListener('waiting', onWaiting)
    audio.addEventListener('stalled', onWaiting)

    return () => {
      clearConnectTimer()
      audio.pause()
      audio.removeAttribute('src')
      audio.load()
      audio.removeEventListener('error', onError)
      audio.removeEventListener('pause', onPause)
      audio.removeEventListener('playing', onPlaying)
      audio.removeEventListener('waiting', onWaiting)
      audio.removeEventListener('stalled', onWaiting)
      audioRef.current = null
    }
  }, [clearConnectTimer])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return
    audio.muted = muted
    if (volumeSupported) audio.volume = volume
  }, [volume, muted, volumeSupported])

  const setVolume = useCallback((value: number) => {
    const next = Math.min(1, Math.max(0, value))
    setVolumeState(next)
    setMuted(next === 0)
  }, [])

  const toggleMute = useCallback(() => {
    setMuted((prev) => {
      if (prev && volume === 0) setVolumeState(0.85)
      return !prev
    })
  }, [volume])

  const toggle = useCallback(async () => {
    const audio = audioRef.current
    if (!audio) return

    if (!audio.paused || loading) {
      clearConnectTimer()
      audio.pause()
      setPlaying(false)
      setLoading(false)
      return
    }

    setError('')
    setLoading(true)

    let url = resolvedRef.current
    if (!url && resolvingRef.current) {
      try {
        url = await resolvingRef.current
      } catch {
        url = ''
      }
    }
    if (!url) {
      setLoading(false)
      setError('Configure o link da radios.net ou do stream no painel administrativo.')
      return
    }

    clearConnectTimer()
    timeoutRef.current = window.setTimeout(() => {
      const current = audioRef.current
      if (current && current.paused === false && current.readyState < 3) {
        current.pause()
        setPlaying(false)
        setLoading(false)
        setError('A rádio demorou a responder. Toque em OUVIR AO VIVO para tentar de novo.')
      }
    }, CONNECT_TIMEOUT_MS)

    try {
      // Recarrega o stream para sempre entrar no ponto ao vivo (e não no áudio antigo em buffer).
      audio.src = url
      audio.load()
      await audio.play()
    } catch (err) {
      console.error('Erro no player:', err)
      clearConnectTimer()
      setPlaying(false)
      setLoading(false)
      setError('O navegador bloqueou o áudio. Toque novamente em OUVIR AO VIVO.')
    }
  }, [clearConnectTimer, loading])

  return { playing, loading, volume, setVolume, muted, toggleMute, volumeSupported, toggle, error }
}
